import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export type ViewAsRole = "agent" | "client";
const KEY = "ct_view_as";
const EVENT = "ct:view_as_changed";

function emit() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
}

export function getViewAs(): ViewAsRole | null {
  if (typeof window === "undefined") return null;
  const v = window.sessionStorage.getItem(KEY);
  return v === "agent" || v === "client" ? v : null;
}

export async function setViewAs(role: ViewAsRole) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(KEY, role);
  emit();
  try {
    const { data } = await supabase.auth.getSession();
    await supabase.from("activity_logs").insert({
      user_id: data.session?.user?.id ?? null,
      action: "admin.view_as_entered",
      target_type: "auth",
      metadata: { target_role: role },
    });
  } catch {
    /* non-blocking */
  }
}

export async function exitViewAs() {
  if (typeof window === "undefined") return;
  const prev = getViewAs();
  window.sessionStorage.removeItem(KEY);
  emit();
  try {
    const { data } = await supabase.auth.getSession();
    await supabase.from("activity_logs").insert({
      user_id: data.session?.user?.id ?? null,
      action: "admin.view_as_exited",
      target_type: "auth",
      metadata: { target_role: prev },
    });
  } catch {
    /* non-blocking */
  }
  window.location.assign("/console");
}

export function clearViewAs() {
  if (typeof window === "undefined") return;
  if (window.sessionStorage.getItem(KEY)) {
    window.sessionStorage.removeItem(KEY);
    emit();
  }
}

function subscribe(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

export function useViewAs(): ViewAsRole | null {
  return useSyncExternalStore(subscribe, getViewAs, () => null);
}

/** Returns true when writes are allowed. Shows a toast and returns false in view-as mode. */
export function assertWritable(): boolean {
  if (getViewAs()) {
    toast.error("Action disabled in preview mode.");
    return false;
  }
  return true;
}